import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { formatCurrency, formatDate, getResponsiveThumbnailUrl } from '@/lib/utils';
import { ordersAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
    CreditCard, 
    Truck, 
    Package, 
    CheckCircle, 
    Loader2,
    DollarSign,
    Ban,
    XCircle,
    ExternalLink,
    Search,
    Filter
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Courier companies list
const courierCompanies = [
    { value: 'fan_courier', label: 'Fan Courier' },
    { value: 'cargus', label: 'Cargus' },
    { value: 'dpd', label: 'DPD' },
    { value: 'sameday', label: 'Sameday' },
    { value: 'gls', label: 'GLS' },
    { value: 'posta_romana', label: 'Poșta Română' },
    { value: 'urgent_cargus', label: 'Urgent Cargus' },
    { value: 'nemo', label: 'Nemo Express' },
    { value: 'other', label: 'Altul' },
];

const AdminOrdersTab = ({ onSetEscrowDialog, onRefresh, initialFilter = {} }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingAction, setLoadingAction] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState(initialFilter.status || 'all');
    const navigate = useNavigate();
    
    // Infinite scroll state
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const perPage = 30;
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    
    // Cancelled listings state
    const [cancelledListings, setCancelledListings] = useState([]);
    const [loadingCancelledListings, setLoadingCancelledListings] = useState(false);
    
    // Status counts state (fetched from backend)
    const [statusCounts, setStatusCounts] = useState({
        all: 0,
        open: 0,
        pending_payment: 0,
        paid: 0,
        shipped: 0,
        delivered: 0,
        completed: 0,
        disputed: 0,
        refunded: 0,
        cancelled: 0,
        cancelled_listings: 0
    });

    // Fetch status counts
    const fetchStatusCounts = useCallback(async () => {
        try {
            const response = await api.get('/admin/orders/status-counts');
            setStatusCounts(response.data);
        } catch (error) {
            console.error('Error fetching status counts:', error);
        }
    }, []);

    // Fetch cancelled listings when filter is 'cancelled_listings'
    const fetchCancelledListings = useCallback(async () => {
        setLoadingCancelledListings(true);
        try {
            const response = await api.get('/admin/listings', { 
                params: { 
                    status: 'cancelled,expired',
                    limit: 100 
                } 
            });
            setCancelledListings(response.data?.listings || []);
        } catch (error) {
            console.error('Error fetching cancelled listings:', error);
            setCancelledListings([]);
        } finally {
            setLoadingCancelledListings(false);
        }
    }, []);

    // Fetch orders function
    const fetchOrders = useCallback(async (pageNum = 1, reset = false) => {
        try {
            if (pageNum === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            const params = {
                skip: (pageNum - 1) * perPage,
                limit: perPage,
            };

            // Add status filter if not 'all'
            if (statusFilter !== 'all') {
                if (statusFilter === 'open') {
                    // For open orders, we'll filter client-side since backend doesn't have this composite filter
                    params.status = 'pending_payment,paid,shipped';
                } else if (statusFilter === 'disputed') {
                    params.status = 'disputed';
                    params.escrow_status = 'disputed';
                } else if (statusFilter === 'refunded') {
                    params.status = 'refunded';
                    params.escrow_status = 'refunded';
                } else {
                    params.status = statusFilter;
                }
            }

            // Add search query
            if (searchQuery.trim()) {
                params.search = searchQuery.trim();
            }

            const response = await api.get('/admin/orders', { params });
            const { orders: newOrders, total } = response.data;

            if (reset || pageNum === 1) {
                setOrders(newOrders);
                setPage(1);
            } else {
                setOrders(prev => [...prev, ...newOrders]);
            }

            setTotalCount(total);
            setHasMore(newOrders.length === perPage);

        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Eroare la încărcarea comenzilor');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [statusFilter, searchQuery]);

    // Load more orders
    const loadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchOrders(nextPage, false);
        }
    }, [page, loadingMore, hasMore, fetchOrders]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        observerRef.current = observer;

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [loadMore, hasMore, loadingMore, loading]);

    // Initial load and filter changes
    useEffect(() => {
        // Fetch status counts on initial load
        fetchStatusCounts();
        
        if (statusFilter === 'cancelled_listings') {
            fetchCancelledListings();
        } else {
            fetchOrders(1, true);
        }
    }, [statusFilter, fetchOrders, fetchCancelledListings, fetchStatusCounts]);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchOrders(1, true);
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [searchQuery, fetchOrders]);

    // Update filter when initialFilter changes
    useEffect(() => {
        if (initialFilter.status) {
            setStatusFilter(initialFilter.status);
        }
    }, [initialFilter.status]);
    
    // Shipping dialog state
    const [shippingDialog, setShippingDialog] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [trackingNumber, setTrackingNumber] = useState('');
    const [courierCompany, setCourierCompany] = useState('');
    
    // Since we're fetching filtered data from API, we use orders directly
    const filteredOrders = orders || [];

    const handleConfirmPayment = async (orderId) => {
        setLoadingAction(`payment-${orderId}`);
        try {
            await ordersAPI.adminConfirmPayment(orderId);
            toast.success('Plata a fost confirmată');
            // Refresh current data
            fetchOrders(1, true);
            onRefresh?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la confirmarea plății');
        } finally {
            setLoadingAction(null);
        }
    };

    const handleConfirmShipped = async (orderId) => {
        // Open dialog instead of directly confirming
        setSelectedOrderId(orderId);
        setTrackingNumber('');
        setCourierCompany('');
        setShippingDialog(true);
    };
    
    const handleSubmitShipping = async () => {
        if (!trackingNumber.trim()) {
            toast.error('Introdu numărul AWB');
            return;
        }
        if (!courierCompany) {
            toast.error('Selectează firma de curierat');
            return;
        }
        
        setLoadingAction(`shipped-${selectedOrderId}`);
        try {
            await ordersAPI.adminConfirmShipped(selectedOrderId, trackingNumber.trim(), courierCompany);
            toast.success('Expedierea a fost confirmată');
            setShippingDialog(false);
            // Refresh current data
            fetchOrders(1, true);
            onRefresh?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la confirmarea expedierii');
        } finally {
            setLoadingAction(null);
        }
    };

    const handleConfirmReceived = async (orderId) => {
        setLoadingAction(`received-${orderId}`);
        try {
            await ordersAPI.adminConfirmReceived(orderId);
            toast.success('Primirea a fost confirmată');
            // Refresh current data
            fetchOrders(1, true);
            onRefresh?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la confirmarea primirii');
        } finally {
            setLoadingAction(null);
        }
    };

    const handleCompleteOrder = async (orderId) => {
        setLoadingAction(`complete-${orderId}`);
        try {
            await ordersAPI.adminMarkCompleted(orderId);
            toast.success('Comanda a fost finalizată și escrow-ul eliberat');
            // Refresh current data
            fetchOrders(1, true);
            onRefresh?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la finalizarea comenzii');
        } finally {
            setLoadingAction(null);
        }
    };

    const handleCancelOrder = async (orderId) => {
        if (!window.confirm('Sigur vrei să anulezi această comandă?')) return;
        
        setLoadingAction(`cancel-${orderId}`);
        try {
            await ordersAPI.adminCancelOrder(orderId);
            toast.success('Comanda a fost anulată');
            // Refresh current data
            fetchOrders(1, true);
            onRefresh?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la anularea comenzii');
        } finally {
            setLoadingAction(null);
        }
    };

    const handleUpdateStatus = async (orderId, newStatus) => {
        setLoadingAction(`status-${orderId}`);
        try {
            await ordersAPI.adminUpdateStatus(orderId, newStatus);
            toast.success(`Status actualizat: ${getStatusLabel(newStatus)}`);
            // Refresh current data
            fetchOrders(1, true);
            onRefresh?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la actualizarea statusului');
        } finally {
            setLoadingAction(null);
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            'pending_payment': 'Așteaptă plata',
            'paid': 'Plătită',
            'shipped': 'Expediată',
            'delivered': 'Livrată',
            'completed': 'Finalizată',
            'cancelled': 'Anulată',
            'disputed': 'În dispută',
            'refunded': 'Rambursată'
        };
        return labels[status] || status;
    };

    const getOrderStatusBadge = (status) => {
        const statusStyles = {
            'pending_payment': 'bg-yellow-100 text-yellow-800',
            'paid': 'bg-blue-100 text-blue-800',
            'shipped': 'bg-purple-100 text-purple-800',
            'delivered': 'bg-green-100 text-green-800',
            'completed': 'bg-green-100 text-green-800',
            'cancelled': 'bg-red-100 text-red-800',
        };
        const statusLabels = {
            'pending_payment': 'Așteaptă plata',
            'paid': 'Plătit',
            'shipped': 'Expediat',
            'delivered': 'Livrat',
            'completed': 'Finalizat',
            'cancelled': 'Anulat',
        };
        return (
            <Badge className={statusStyles[status] || 'bg-gray-100 text-gray-800'}>
                {statusLabels[status] || status}
            </Badge>
        );
    };
    
    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <CardTitle>Vânzări</CardTitle>
                                <CardDescription>Gestionează vânzările și plățile</CardDescription>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                {/* Status Filter */}
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-full sm:w-52">
                                        <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                                        <SelectValue placeholder="Filtrează după status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            Toate comenzile ({statusCounts.all})
                                        </SelectItem>
                                        <SelectItem value="open">
                                            🟡 Deschise ({statusCounts.open})
                                        </SelectItem>
                                        <SelectItem value="pending_payment">
                                            ⏳ Așteaptă plata ({statusCounts.pending_payment})
                                        </SelectItem>
                                        <SelectItem value="paid">
                                            💳 Plătite ({statusCounts.paid})
                                        </SelectItem>
                                        <SelectItem value="shipped">
                                            🚚 Expediate ({statusCounts.shipped})
                                        </SelectItem>
                                        <SelectItem value="delivered">
                                            📦 Livrate ({statusCounts.delivered})
                                        </SelectItem>
                                        <SelectItem value="completed">
                                            ✅ Finalizate ({statusCounts.completed})
                                        </SelectItem>
                                        <SelectItem value="disputed">
                                            ⚠️ În dispută ({statusCounts.disputed})
                                        </SelectItem>
                                        <SelectItem value="refunded">
                                            ↩️ Rambursate ({statusCounts.refunded})
                                        </SelectItem>
                                        <SelectItem value="cancelled">
                                            ❌ Anulate ({statusCounts.cancelled})
                                        </SelectItem>
                                        <SelectItem value="cancelled_listings">
                                            📦 Listări anulate ({statusCounts.cancelled_listings})
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                
                                {/* Search */}
                                <div className="relative w-full sm:w-72">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder="Caută după ID sau nume produs..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Active filter indicator */}
                        {statusFilter !== 'all' && (
                            <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="flex items-center gap-1">
                                    <Filter className="w-3 h-3" />
                                    {statusFilter === 'open' ? 'Comenzi deschise' :
                                     statusFilter === 'pending_payment' ? 'Așteaptă plata' :
                                     statusFilter === 'paid' ? 'Plătite' :
                                     statusFilter === 'shipped' ? 'Expediate' :
                                     statusFilter === 'delivered' ? 'Livrate' :
                                     statusFilter === 'completed' ? 'Finalizate' :
                                     statusFilter === 'disputed' ? 'În dispută' :
                                     statusFilter === 'refunded' ? 'Rambursate' :
                                     statusFilter === 'cancelled' ? 'Anulate' :
                                     statusFilter === 'cancelled_listings' ? 'Listări anulate' : statusFilter}
                                    <button 
                                        onClick={() => setStatusFilter('all')}
                                        className="ml-1 hover:text-destructive"
                                    >
                                        ×
                                    </button>
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                    {filteredOrders.length} {filteredOrders.length === 1 ? 'comandă' : 'comenzi'}
                                </span>
                            </div>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {/* Loading state */}
                    {loading && orders.length === 0 && statusFilter !== 'cancelled_listings' && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin mr-2" />
                            <span>Se încarcă comenzile...</span>
                        </div>
                    )}
                    
                    {/* Cancelled Listings view */}
                    {statusFilter === 'cancelled_listings' && (
                        <div>
                            {loadingCancelledListings ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                                    <span>Se încarcă listările anulate...</span>
                                </div>
                            ) : cancelledListings.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>Nu există listări anulate</p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {cancelledListings.map((listing) => (
                                        <div 
                                            key={listing.listing_id}
                                            className="p-4 cursor-pointer hover:bg-muted/30 flex items-center gap-4"
                                            onClick={() => navigate(`/listing/${listing.slug || listing.listing_id}`)}
                                        >
                                            {listing.images?.[0] ? (
                                                <img 
                                                    src={getResponsiveThumbnailUrl(listing.images[0], 56, 56, 75)} 
                                                    alt={listing.title} 
                                                    className="w-14 h-14 rounded-lg object-cover shrink-0"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                                    <Package className="w-6 h-6 text-gray-400" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="font-medium truncate">{listing.title}</span>
                                                    <span className="font-bold shrink-0">
                                                        {formatCurrency(listing.price || listing.current_price || listing.starting_price)}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                    Vânzător: {listing.seller_name || listing.seller_username}
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Badge variant={listing.status === 'cancelled' ? 'destructive' : 'secondary'}>
                                                        {listing.status === 'cancelled' ? 'Anulată' : 'Expirată'}
                                                    </Badge>
                                                    <Badge variant="outline">
                                                        {listing.listing_type === 'auction' ? 'Licitație' : 'Preț fix'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Mobile: Card Layout - only show when not viewing cancelled listings */}
                    {statusFilter !== 'cancelled_listings' && (
                    <div className="md:hidden divide-y">
                        {filteredOrders.map((o) => {
                            const imageUrl = o.listing_image 
                                ? getResponsiveThumbnailUrl(o.listing_image, 80, 60, 75)
                                : null;
                            return (
                                <div 
                                    key={o.order_id}
                                    className="p-4 cursor-pointer hover:bg-muted/30"
                                    onClick={() => navigate(`/order/${o.order_id}`)}
                                >
                                    <div className="flex items-start gap-3">
                                        {imageUrl ? (
                                            <img 
                                                src={imageUrl} 
                                                alt={o.listing_title} 
                                                className="w-14 h-14 rounded-lg object-cover shrink-0"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                                <Package className="w-6 h-6 text-gray-400" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-medium text-sm truncate">{o.listing_title?.slice(0, 30)}</span>
                                                <span className="font-bold text-sm shrink-0">{formatCurrency(o.amount)}</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                                <span className="font-medium text-foreground">{o.seller_name || o.seller_username}</span>
                                                <span className="text-muted-foreground">→</span>
                                                <span className="font-medium text-foreground">{o.buyer_name || o.buyer_username}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                {o.order_type === 'auction' && (
                                                    <Badge className="bg-blue-500 text-white text-[10px] px-1.5 py-0">
                                                        Licitație
                                                    </Badge>
                                                )}
                                                {getOrderStatusBadge(o.order_status)}
                                                <Badge className={
                                                    o.escrow_status === 'released' ? 'bg-green-100 text-green-800' :
                                                    o.escrow_status === 'disputed' ? 'bg-red-100 text-red-800' :
                                                    o.escrow_status === 'held' ? 'bg-yellow-100 text-yellow-800' :
                                                    o.escrow_status === 'refunded' ? 'bg-orange-100 text-orange-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }>
                                                    {o.escrow_status === 'held' ? 'Escrow' :
                                                     o.escrow_status === 'released' ? 'Eliberat' :
                                                     o.escrow_status === 'refunded' ? 'Rambursat' :
                                                     o.escrow_status === 'disputed' ? 'Dispută' :
                                                     o.escrow_status}
                                                </Badge>
                                            </div>
                                            {/* Status dropdown for mobile */}
                                            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                                                <Select
                                                    value={o.order_status}
                                                    onValueChange={(value) => handleUpdateStatus(o.order_id, value)}
                                                    disabled={loadingAction === `status-${o.order_id}`}
                                                >
                                                    <SelectTrigger className="w-full h-8 text-xs">
                                                        {loadingAction === `status-${o.order_id}` ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            <SelectValue placeholder="Schimbă status" />
                                                        )}
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="pending_payment">Așteaptă plata</SelectItem>
                                                        <SelectItem value="paid">Plătită</SelectItem>
                                                        <SelectItem value="shipped">Expediată</SelectItem>
                                                        <SelectItem value="delivered">Livrată</SelectItem>
                                                        <SelectItem value="completed">Finalizată</SelectItem>
                                                        <SelectItem value="cancelled">Anulată</SelectItem>
                                                        <SelectItem value="disputed">În dispută</SelectItem>
                                                        <SelectItem value="refunded">Rambursată</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-xs text-muted-foreground font-mono">#{o.order_id.slice(-6)}</span>
                                                <span className="text-xs text-muted-foreground">{formatDate(o.created_at)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        
                        {/* Load more trigger for mobile */}
                        {hasMore && !loading && (
                            <div ref={loadMoreRef} className="p-4 text-center">
                                {loadingMore ? (
                                    <div className="flex items-center justify-center">
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        <span className="text-sm text-muted-foreground">Se încarcă mai multe comenzi...</span>
                                    </div>
                                ) : (
                                    <span className="text-sm text-muted-foreground">Derulează pentru mai multe comenzi</span>
                                )}
                            </div>
                        )}
                    </div>
                    )}
                    
                    {/* Desktop: Table Layout */}
                    {statusFilter !== 'cancelled_listings' && (
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-muted/50">
                                <tr>
                                    <th className="text-left p-4">ID</th>
                                    <th className="text-left p-4">Produs</th>
                                    <th className="text-left p-4">Vânzător → Cumpărător</th>
                                    <th className="text-left p-4">Sumă</th>
                                    <th className="text-left p-4">Status</th>
                                    <th className="text-left p-4">Fonduri</th>
                                    <th className="text-left p-4 hidden lg:table-cell">Data</th>
                                    <th className="text-right p-4">Acțiuni Admin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map((o) => {
                                    const imageUrl = o.listing_image 
                                        ? getResponsiveThumbnailUrl(o.listing_image, 80, 60, 75)
                                        : null;
                                    return (
                                    <tr 
                                        key={o.order_id} 
                                        className="border-t hover:bg-muted/30 cursor-pointer"
                                        onClick={() => navigate(`/order/${o.order_id}`)}
                                    >
                                        <td className="p-4 font-mono text-sm">
                                            <span className="flex items-center gap-1 text-primary">
                                                {o.order_id.slice(-8)}
                                                <ExternalLink className="w-3 h-3" />
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                {imageUrl ? (
                                                    <img 
                                                        src={imageUrl} 
                                                        alt={o.listing_title} 
                                                        className="w-10 h-10 rounded-lg object-cover shrink-0"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                                        <Package className="w-5 h-5 text-gray-400" />
                                                    </div>
                                                )}
                                                <span className="text-sm max-w-[150px] truncate">{o.listing_title?.slice(0, 40)}</span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-1 text-sm">
                                                <span className="font-medium">{o.seller_name || o.seller_username}</span>
                                                <span className="text-muted-foreground">→</span>
                                                <span className="font-medium">{o.buyer_name || o.buyer_username}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 font-medium">{formatCurrency(o.amount)}</td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-1">
                                                {o.order_type === 'auction' && (
                                                    <Badge className="bg-blue-500 text-white text-[10px] px-1.5 py-0 mr-1">
                                                        Licitație
                                                    </Badge>
                                                )}
                                                {getOrderStatusBadge(o.order_status)}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <Badge className={
                                                o.escrow_status === 'released' ? 'bg-green-100 text-green-800' :
                                                o.escrow_status === 'disputed' ? 'bg-red-100 text-red-800' :
                                                o.escrow_status === 'held' ? 'bg-yellow-100 text-yellow-800' :
                                                o.escrow_status === 'refunded' ? 'bg-orange-100 text-orange-800' :
                                                'bg-gray-100 text-gray-800'
                                            }>
                                                {o.escrow_status === 'held' ? 'În escrow' :
                                                 o.escrow_status === 'released' ? 'Eliberat' :
                                                 o.escrow_status === 'refunded' ? 'Rambursat' :
                                                 o.escrow_status === 'disputed' ? 'În dispută' :
                                                 o.escrow_status}
                                            </Badge>
                                        </td>
                                        <td className="p-4 text-sm text-muted-foreground hidden lg:table-cell">{formatDate(o.created_at)}</td>
                                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex justify-end gap-2 flex-wrap items-center">
                                                {/* Status dropdown for admin */}
                                                <Select
                                                    value={o.order_status}
                                                    onValueChange={(value) => handleUpdateStatus(o.order_id, value)}
                                                    disabled={loadingAction === `status-${o.order_id}`}
                                                >
                                                    <SelectTrigger className="w-[140px] h-8 text-xs">
                                                        {loadingAction === `status-${o.order_id}` ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            <SelectValue />
                                                        )}
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="pending_payment">Așteaptă plata</SelectItem>
                                                        <SelectItem value="paid">Plătită</SelectItem>
                                                        <SelectItem value="shipped">Expediată</SelectItem>
                                                        <SelectItem value="delivered">Livrată</SelectItem>
                                                        <SelectItem value="completed">Finalizată</SelectItem>
                                                        <SelectItem value="cancelled">Anulată</SelectItem>
                                                        <SelectItem value="disputed">În dispută</SelectItem>
                                                        <SelectItem value="refunded">Rambursată</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                
                                                {/* Confirm Payment button - shown when pending_payment */}
                                                {o.order_status === 'pending_payment' && (
                                                    <>
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline" 
                                                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                                            onClick={() => handleConfirmPayment(o.order_id)}
                                                            disabled={loadingAction === `payment-${o.order_id}`}
                                                        >
                                                            {loadingAction === `payment-${o.order_id}` ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <CreditCard className="w-4 h-4 mr-1" />
                                                                    Confirmă plata
                                                                </>
                                                            )}
                                                        </Button>
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline" 
                                                            className="text-red-600 border-red-300 hover:bg-red-50"
                                                            onClick={() => handleCancelOrder(o.order_id)}
                                                            disabled={loadingAction === `cancel-${o.order_id}`}
                                                        >
                                                            {loadingAction === `cancel-${o.order_id}` ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <XCircle className="w-4 h-4 mr-1" />
                                                                    Anulează
                                                                </>
                                                            )}
                                                        </Button>
                                                    </>
                                                )}

                                                {/* Confirm Shipped button - shown when paid */}
                                                {o.order_status === 'paid' && (
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                                        onClick={() => handleConfirmShipped(o.order_id)}
                                                        disabled={loadingAction === `shipped-${o.order_id}`}
                                                    >
                                                        {loadingAction === `shipped-${o.order_id}` ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Truck className="w-4 h-4 mr-1" />
                                                                Confirmă expediere
                                                            </>
                                                        )}
                                                    </Button>
                                                )}

                                                {/* Confirm Received button - shown when shipped */}
                                                {o.order_status === 'shipped' && (
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                                                        onClick={() => handleConfirmReceived(o.order_id)}
                                                        disabled={loadingAction === `received-${o.order_id}`}
                                                    >
                                                        {loadingAction === `received-${o.order_id}` ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Package className="w-4 h-4 mr-1" />
                                                                Confirmă primire
                                                            </>
                                                        )}
                                                    </Button>
                                                )}

                                                {/* Complete Order button - shown when delivered or when escrow is held */}
                                                {(o.order_status === 'delivered' || (o.escrow_status === 'held' && o.order_status !== 'pending_payment')) && o.order_status !== 'completed' && (
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        className="text-green-600 border-green-300 hover:bg-green-50"
                                                        onClick={() => handleCompleteOrder(o.order_id)}
                                                        disabled={loadingAction === `complete-${o.order_id}`}
                                                    >
                                                        {loadingAction === `complete-${o.order_id}` ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                                Finalizează
                                                            </>
                                                        )}
                                                    </Button>
                                                )}

                                                {/* Escrow management buttons for disputes */}
                                                {o.escrow_status === 'disputed' && (
                                                    <>
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline" 
                                                            className="text-green-600" 
                                                            onClick={() => onSetEscrowDialog({ open: true, order: o, action: 'release' })}
                                                        >
                                                            <DollarSign className="w-4 h-4 mr-1" />
                                                            Eliberează
                                                        </Button>
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline" 
                                                            className="text-red-600" 
                                                            onClick={() => onSetEscrowDialog({ open: true, order: o, action: 'refund' })}
                                                        >
                                                            <Ban className="w-4 h-4 mr-1" />
                                                            Rambursează
                                                        </Button>
                                                    </>
                                                )}

                                                {/* Show completed status */}
                                                {o.order_status === 'completed' && (
                                                    <span className="text-green-600 text-sm flex items-center gap-1">
                                                        <CheckCircle className="w-4 h-4" />
                                                        Finalizat
                                                    </span>
                                                )}

                                                {/* Cancel order button - for all non-completed/cancelled orders */}
                                                {!['completed', 'cancelled'].includes(o.order_status) && (
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        className="text-red-600 border-red-300 hover:bg-red-50"
                                                        onClick={() => handleCancelOrder(o.order_id)}
                                                        disabled={loadingAction === `cancel-${o.order_id}`}
                                                    >
                                                        {loadingAction === `cancel-${o.order_id}` ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <XCircle className="w-4 h-4 mr-1" />
                                                                Anulează
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        
                        {/* Load more trigger for desktop */}
                        {hasMore && !loading && (
                            <div ref={loadMoreRef} className="p-4 text-center border-t">
                                {loadingMore ? (
                                    <div className="flex items-center justify-center">
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        <span className="text-sm text-muted-foreground">Se încarcă mai multe comenzi...</span>
                                    </div>
                                ) : (
                                    <span className="text-sm text-muted-foreground">Derulează pentru mai multe comenzi</span>
                                )}
                            </div>
                        )}
                    </div>
                    )}
                    
                    {/* Empty state - shown for both mobile and desktop */}
                    {filteredOrders.length === 0 && !loading && (
                        <div className="text-center py-12 text-muted-foreground">
                            {searchQuery.trim() ? (
                                <>
                                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p>Nu s-au găsit comenzi pentru &quot;{searchQuery}&quot;</p>
                                </>
                            ) : (
                                <p>Nu există comenzi</p>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
            
            {/* Shipping Dialog */}
            <Dialog open={shippingDialog} onOpenChange={setShippingDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirmă expedierea</DialogTitle>
                        <DialogDescription>
                            Selectează firma de curierat și introdu numărul AWB
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div>
                            <p className="text-sm font-medium mb-2">Firma de curierat</p>
                            <select
                                value={courierCompany}
                                onChange={(e) => setCourierCompany(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
                            >
                                <option value="">Selectează curierul</option>
                                {courierCompanies.map((courier) => (
                                    <option key={courier.value} value={courier.value}>
                                        {courier.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <p className="text-sm font-medium mb-2">Număr AWB</p>
                            <input
                                type="text"
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                placeholder="Ex: 2093847562"
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShippingDialog(false)}>
                            Anulează
                        </Button>
                        <Button 
                            onClick={handleSubmitShipping} 
                            disabled={loadingAction?.startsWith('shipped-') || !courierCompany || !trackingNumber.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            {loadingAction?.startsWith('shipped-') ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Truck className="w-4 h-4 mr-2" />
                            )}
                            Confirmă expedierea
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminOrdersTab;
